/**
 * Add to cart button: a cart icon, the label, and what pressing it will cost.
 *
 * The number is the whole basket the form is about to post, not the product
 * price: the chosen variant (or the model the picker points at instead),
 * multiplied by the quantity, plus every ticked add-on at its own quantity.
 * That is exactly what /cart/add.js receives, so the button cannot promise one
 * figure and charge another.
 *
 * The awkward part is that the theme owns the button's text. On every variant
 * change the variant picker writes `addBtn.textContent = ...`, which replaces
 * the button's children with a single text node - any markup put inside would
 * be wiped the first time somebody picks an option, and again for every sold
 * out or preselection message. So this element does not fight for ownership:
 * it watches the button, takes whatever text the theme last wrote as the
 * label, and rebuilds around it. The theme keeps saying what the button says;
 * this only decides how it is laid out.
 */
if (!customElements.get('atc-total')) {
  class AtcTotal extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[name="add"]');
      if (!this.button) return;

      this.section = this.closest('.shopify-section') || document;
      this.moneyFormat = this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
      this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
      this.baseCompare = parseInt(this.dataset.baseCompare, 10) || 0;

      // Whatever the button says right now, before anything is rebuilt
      this.label = this.button.textContent.trim();

      this.onVariantChange = this.handleVariantChange.bind(this);
      this.onInput = this.handleInput.bind(this);

      this.section.addEventListener('on:variant:change', this.onVariantChange);
      // Add-ons and the model picker are plain inputs in the same section, and
      // the quantity stepper fires input rather than change while held down.
      this.section.addEventListener('change', this.onInput);
      this.section.addEventListener('input', this.onInput);

      // The theme rewrites the label, not this element, so the only reliable
      // signal is the button's own children changing.
      this.observer = new MutationObserver(() => this.absorbLabel());
      this.observer.observe(this.button, { childList: true, characterData: true, subtree: true });

      this.render();
    }

    disconnectedCallback() {
      this.section.removeEventListener('on:variant:change', this.onVariantChange);
      this.section.removeEventListener('change', this.onInput);
      this.section.removeEventListener('input', this.onInput);
      this.observer?.disconnect();
    }

    /**
     * Picks the theme's freshly written text back up as the label and rebuilds.
     * Guarded, because render() writes to the button too and would otherwise
     * see its own work and loop.
     */
    absorbLabel() {
      const text = this.button.textContent.trim();
      if (!text) return;
      this.label = text;
      this.render();
    }

    handleVariantChange(evt) {
      const variant = evt.detail?.variant;
      // No variant means sold out or an incomplete selection; the theme has
      // already said so in the label, and a price beside that would be a lie.
      this.basePrice = variant ? variant.price : null;
      this.baseCompare = variant ? variant.compare_at_price || 0 : 0;
      this.render();
    }

    /** @returns {number} quantity in the form, 1 when there is no stepper */
    get quantity() {
      const input = this.form?.querySelector('input[name="quantity"]');
      const value = parseInt(input?.value, 10);
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    get form() {
      return this.dataset.formId ? document.getElementById(this.dataset.formId) : this.button.form;
    }

    /**
     * The model picker can point the form at a different product entirely, in
     * which case that product's price is the one being added, not the variant
     * the page was built around.
     * @returns {number|null}
     */
    get chosenModel() {
      return this.section.querySelector('.model-picker__input:checked');
    }

    get modelPrice() {
      const price = parseInt(this.chosenModel?.dataset.modelPrice, 10);
      return Number.isFinite(price) ? price : null;
    }

    get modelCompare() {
      const compare = parseInt(this.chosenModel?.dataset.modelCompare, 10);
      return Number.isFinite(compare) ? compare : null;
    }

    /** @returns {number} the ticked extras, each at its own quantity */
    addonsTotal(field) {
      const inputs = this.section.querySelectorAll('.product-addons__input:checked:not([disabled])');
      return Array.from(inputs).reduce((sum, input) => {
        const price = parseInt(input.dataset[field], 10) || parseInt(input.dataset.addonPrice, 10) || 0;
        const qty = parseInt(input.dataset.addonQuantity, 10) || 1;
        return sum + price * qty;
      }, 0);
    }

    /** @returns {number|null} null when there is nothing honest to show */
    get total() {
      const base = this.modelPrice ?? this.basePrice;
      if (base === null || base === undefined) return null;
      return base * this.quantity + this.addonsTotal('addonPrice');
    }

    /**
     * The same basket at its compare-at prices. Anything without one counts at
     * its own price, so the crossed-out figure is a real was-price for the
     * whole selection and never smaller than what is being charged.
     * @returns {number|null} null when nothing in the basket was ever dearer
     */
    get compareTotal() {
      const base = this.modelCompare ?? this.baseCompare;
      const price = this.modelPrice ?? this.basePrice;
      if (!base || price === null || price === undefined) return null;
      const total = Math.max(base, price) * this.quantity + this.addonsTotal('addonCompare');
      return total > this.total ? total : null;
    }

    /**
     * Only three things move the number. Every other keystroke in the section -
     * a gift card message, a search box - must not rebuild the button.
     * @param {object} evt - Event object.
     */
    handleInput(evt) {
      const el = evt.target;
      if (!el.matches?.('.product-addons__input, .model-picker__input, input[name="quantity"]')) return;
      this.render();
    }

    render() {
      // The theme marks a submit in flight with aria-disabled and a spinner.
      // Rebuilding the button underneath that is pointless and can only flicker.
      if (this.button.getAttribute('aria-disabled') === 'true') return;

      const showTotal = this.dataset.showTotal !== 'false';
      const total = showTotal && !this.button.disabled ? this.total : null;

      // Stop watching while writing. The observer fires on a microtask, long
      // after a plain `this.rendering = false` at the end of this method would
      // have cleared itself - disconnecting is the only guard that actually
      // holds, and without it the element can chase its own writes.
      this.observer?.disconnect();
      this.button.textContent = '';

      if (this.dataset.showIcon !== 'false' && this.iconMarkup) {
        const icon = document.createElement('span');
        icon.className = 'atc-total__icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = this.iconMarkup;
        this.button.appendChild(icon);
      }

      const label = document.createElement('span');
      label.className = 'atc-total__label';
      label.setAttribute('data-atc-label', '');
      label.textContent = this.label;
      this.button.appendChild(label);

      if (total !== null) {
        const amount = document.createElement('span');
        amount.className = 'atc-total__amount';
        amount.setAttribute('data-atc-total', '');
        amount.textContent = this.formatMoney(total);
        this.button.appendChild(amount);

        const compare = this.dataset.showCompare === 'true' ? this.compareTotal : null;
        if (compare !== null) {
          const was = document.createElement('s');
          was.className = 'atc-total__was';
          was.textContent = this.formatMoney(compare);
          amount.appendChild(was);
        }
      }

      this.observer?.observe(this.button, { childList: true, characterData: true, subtree: true });
    }

    /** @returns {string} the cart icon, copied once out of the template tag */
    get iconMarkup() {
      if (this.icon === undefined) {
        this.icon = this.querySelector('template[data-atc-icon]')?.innerHTML.trim() || null;
      }
      return this.icon;
    }

    /**
     * The theme has no money formatter in JS, so this reads the shop's own
     * money_format rather than guessing at separators - a store that prints
     * 1.234,56 must not suddenly see 1,234.56 on its most important button.
     * @param {number} cents - Amount in cents.
     * @returns {string}
     */
    formatMoney(cents) {
      const format = this.moneyFormat;
      const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
      const name = placeholder ? placeholder[1] : 'amount';

      const withDecimals = !name.includes('no_decimals');
      const commaSeparator = name.includes('comma_separator');
      const value = cents / 100;

      let number = value.toFixed(withDecimals ? 2 : 0);
      let [whole, fraction] = number.split('.');
      whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, commaSeparator ? '.' : ',');
      number = fraction ? whole + (commaSeparator ? ',' : '.') + fraction : whole;

      return format.replace(/\{\{\s*\w+\s*\}\}/, number).replace(/<[^>]+>/g, '');
    }
  }

  customElements.define('atc-total', AtcTotal);
}
